import React, { useState, useRef, useEffect } from "react";
import dayjs from "dayjs";

const CustomDateRangePicker = ({
  startDate,
  endDate,
  onDateRangeChange,
  onApply,
  onCancel,
  isOpen,
  onToggle,
  className = "",
  calendarClassName = "",
  presets = [],
  placeholder = "Select Date Range",
  maxDate = new Date(), // Cannot select future date
}) => {
  const [currentMonth, setCurrentMonth] = useState(dayjs().month());
  const [currentYear, setCurrentYear] = useState(dayjs().year());
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const [selectingStart, setSelectingStart] = useState(true);
  const [hoveredDate, setHoveredDate] = useState(null);
  const [activePresetIndex, setActivePresetIndex] = useState(null);
  const calendarRef = useRef(null);

  // Default presets if none provided
  const defaultPresets = [
    {
      label: "This Week",
      getValue: () => ({
        start: dayjs().startOf("week").toDate(),
        end: dayjs().endOf("week").toDate(),
      }),
    },
    {
      label: "Last Week",
      getValue: () => ({
        start: dayjs().subtract(1, "week").startOf("week").toDate(),
        end: dayjs().subtract(1, "week").endOf("week").toDate(),
      }),
    },
    {
      label: "This Month",
      getValue: () => ({
        start: dayjs().startOf("month").toDate(),
        end: dayjs().endOf("month").toDate(),
      }),
    },
    {
      label: "Last Month",
      getValue: () => ({
        start: dayjs().subtract(1, "month").startOf("month").toDate(),
        end: dayjs().subtract(1, "month").endOf("month").toDate(),
      }),
    },
    {
      label: "Last 3 Months",
      getValue: () => ({
        start: dayjs().subtract(3, "months").startOf("month").toDate(),
        end: dayjs().endOf("month").toDate(),
      }),
    },
    {
      label: "Last 6 Months",
      getValue: () => ({
        start: dayjs().subtract(6, "months").startOf("month").toDate(),
        end: dayjs().endOf("month").toDate(),
      }),
    },
    {
      label: "This Year",
      getValue: () => ({
        start: dayjs().startOf("year").toDate(),
        end: dayjs().endOf("year").toDate(),
      }),
    },
  ];

  const availablePresets = presets.length > 0 ? presets : defaultPresets;

  useEffect(() => {
    if (startDate) {
      setTempStartDate(startDate);
    }
    if (endDate) {
      setTempEndDate(endDate);
    }
    // Don't reset activePresetIndex here - let it persist to show active state
  }, [startDate, endDate]);

  // Generate calendar days
  const generateCalendarDays = () => {
    const startOfMonth = dayjs()
      .year(currentYear)
      .month(currentMonth)
      .startOf("month");
    const endOfMonth = dayjs()
      .year(currentYear)
      .month(currentMonth)
      .endOf("month");
    const startOfCalendar = startOfMonth.clone().startOf("week");
    const endOfCalendar = endOfMonth.clone().endOf("week");

    const days = [];
    const current = startOfCalendar.clone();

    while (current.isSameOrBefore(endOfCalendar)) {
      days.push(current.clone());
      current.add(1, "day");
    }

    return days;
  };

  const handleDateClick = (date, event) => {
    event.stopPropagation(); // Prevent event bubbling
    const clickedDate = date.toDate();

    // Cannot select future dates
    if (dayjs(clickedDate).isAfter(dayjs(maxDate))) {
      return;
    }

    // Reset active preset when manually selecting dates
    setActivePresetIndex(null);

    if (selectingStart || !tempStartDate) {
      setTempStartDate(clickedDate);
      setTempEndDate(null);
      setSelectingStart(false);
    } else {
      if (dayjs(clickedDate).isBefore(dayjs(tempStartDate))) {
        setTempStartDate(clickedDate);
        setTempEndDate(tempStartDate);
      } else {
        setTempEndDate(clickedDate);
      }
      setSelectingStart(true);
    }
  };

  const handlePresetClick = (preset, event, index) => {
    event.stopPropagation(); // Prevent event bubbling
    const { start, end } = preset.getValue();

    // Validate that end date is not in the future
    const today = dayjs(maxDate);
    const validatedEnd = dayjs(end).isAfter(today) ? today.toDate() : end;

    // Ensure start date is also not in the future
    const validatedStart = dayjs(start).isAfter(today)
      ? today.toDate()
      : start;

    setTempStartDate(validatedStart);
    setTempEndDate(validatedEnd);

    // Set the active preset index
    setActivePresetIndex(index);
  };
  const handleApply = () => {
    if (onApply) {
      onApply(tempStartDate, tempEndDate);
    }
  };

  const handleCancel = () => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setActivePresetIndex(null);
    if (onCancel) {
      onCancel();
    }
  };

  const handleDateHover = (date) => {
    setHoveredDate(date.toDate());
  };

  const handleDateLeave = () => {
    setHoveredDate(null);
  };

  const isDateInHoverRange = (date) => {
    // Only show hover range when we have a start date but no end date (selecting end date)
    if (!tempStartDate || tempEndDate || selectingStart || !hoveredDate) {
      return false;
    }

    const dateToCheck = date.toDate();
    const start = dayjs(tempStartDate).isBefore(dayjs(hoveredDate))
      ? tempStartDate
      : hoveredDate;
    const end = dayjs(tempStartDate).isBefore(dayjs(hoveredDate))
      ? hoveredDate
      : tempStartDate;

    return dayjs(dateToCheck).isBetween(
      dayjs(start),
      dayjs(end),
      "day",
      "[]"
    );
  };

  const isDateInRange = (date) => {
    if (!tempStartDate || !tempEndDate) return false;
    return dayjs(date).isBetween(
      dayjs(tempStartDate),
      dayjs(tempEndDate),
      "day",
      "[]"
    );
  };

  const isDateSelected = (date) => {
    const dateToCheck = date.toDate();
    return (
      (tempStartDate &&
        dayjs(dateToCheck).isSame(dayjs(tempStartDate), "day")) ||
      (tempEndDate && dayjs(dateToCheck).isSame(dayjs(tempEndDate), "day"))
    );
  };

  const formatDateRange = () => {
    if (startDate && endDate) {
      return `${dayjs(startDate).format("DD MMM, YYYY")} - ${dayjs(
        endDate
      ).format("DD MMM, YYYY")}`;
    }
    if (startDate) {
      return dayjs(startDate).format("DD MMM, YYYY");
    }
    return placeholder;
  };

  const navigateMonth = (direction) => {
    if (direction === "prev") {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  const handleMonthChange = (e) => {
    setCurrentMonth(parseInt(e.target.value));
  };

  const handleYearChange = (e) => {
    setCurrentYear(parseInt(e.target.value));
  };

  // Generate year options (current year and previous years)
  const generateYearOptions = () => {
    const years = [];
    const currentYearValue = dayjs().year();
    for (let i = currentYearValue; i >= currentYearValue - 10; i--) {
      years.push(i);
    }
    return years;
  };

  const calendarDays = generateCalendarDays();

  if (!isOpen) {
    return (
      <button
        onClick={onToggle}
        className={`flex items-center justify-between w-full px-4 py-2 border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${className}`}
      >
        <span className="text-sm text-gray-700">{formatDateRange()}</span>
        <svg
          className="w-5 h-5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </button>
    );
  }

  return (
    <div
      className={`absolute z-50 bg-white border border-gray-200 rounded-lg shadow-lg ${calendarClassName}`}
      ref={calendarRef}
      onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
    >
      <div
        className="flex"
        onClick={(e) => e.stopPropagation()} // Additional prevention for nested elements
      >
        {/* Presets Panel */}
        {availablePresets.length > 0 && (
          <div className="w-36 border-r border-gray-200 p-3">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">
              Quick Select
            </h3>
            <div className="space-y-1">
              {availablePresets.map((preset, index) => (
                <button
                  key={index}
                  onClick={(e) => handlePresetClick(preset, e, index)}
                  className={`w-full text-left px-2 py-1.5 text-sm font-medium rounded-md transition-all duration-200 hover:shadow-sm ${
                    activePresetIndex === index
                      ? "bg-orange-500 text-white hover:bg-orange-600 shadow-md hover:shadow-lg hover:scale-105"
                      : "text-gray-700 hover:bg-orange-50 hover:text-orange-700"
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Calendar Panel */}
        <div className="p-4 w-72">
          {/* Month/Year Navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigateMonth("prev");
              }}
              className="p-1 hover:bg-orange-50 hover:text-orange-600 rounded-md transition-all duration-200 hover:shadow-sm"
            >
              <svg
                className="w-5 h-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <div
              className="flex items-center space-x-2"
              onClick={(e) => e.stopPropagation()}
            >
              <select
                value={currentMonth}
                onChange={handleMonthChange}
                onClick={(e) => e.stopPropagation()}
                className="text-sm font-medium text-gray-900 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500 hover:border-orange-300 transition-all duration-200"
              >
                {dayjs.months().map((month, index) => (
                  <option key={index} value={index}>
                    {month}
                  </option>
                ))}
              </select>

              <select
                value={currentYear}
                onChange={handleYearChange}
                onClick={(e) => e.stopPropagation()}
                className="text-sm font-medium text-gray-900 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500 hover:border-orange-300 transition-all duration-200"
              >
                {generateYearOptions().map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation();
                navigateMonth("next");
              }}
              className="p-1 hover:bg-orange-50 hover:text-orange-600 rounded-md transition-all duration-200 hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-600"
              disabled={
                currentYear === dayjs().year() &&
                currentMonth === dayjs().month()
              }
            >
              <svg
                className="w-5 h-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>

          {/* Days of Week Header */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
              <div
                key={day}
                className="text-center text-xs font-medium text-gray-500 py-2"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((date, index) => {
              const isCurrentMonth = date.month() === currentMonth;
              const isToday = date.isSame(dayjs(), "day");
              const isSelected = isDateSelected(date);
              const isInRange = isDateInRange(date);
              const isInHoverRange = isDateInHoverRange(date);
              const isFuture = dayjs(date).isAfter(dayjs(maxDate));
              const isDisabled = isFuture || !isCurrentMonth;
              const isStartDate =
                tempStartDate &&
                dayjs(date).isSame(dayjs(tempStartDate), "day");
              const isEndDate =
                tempEndDate && dayjs(date).isSame(dayjs(tempEndDate), "day");
              const isHovered =
                hoveredDate && dayjs(date).isSame(dayjs(hoveredDate), "day");

              return (
                <button
                  key={index}
                  onClick={(e) => !isDisabled && handleDateClick(date, e)}
                  onMouseEnter={() => !isDisabled && handleDateHover(date)}
                  onMouseLeave={handleDateLeave}
                  disabled={isDisabled}
                  className={`
                    h-8 text-sm font-medium rounded-md transition-all duration-200 relative
                    ${
                      isDisabled
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-gray-900 cursor-pointer"
                    }
                    ${
                      isStartDate || isEndDate
                        ? isHovered
                          ? "bg-brown-600 text-white shadow-lg transform scale-110 ring-2 ring-brown-300"
                          : "bg-brown-400 text-white hover:bg-brown-600 shadow-md hover:shadow-lg hover:scale-105"
                        : !isDisabled && !isInRange && !isInHoverRange
                        ? "hover:bg-blue-500 hover:scale-105"
                        : ""
                    }
                    ${
                      (isInRange || isInHoverRange) &&
                      !isSelected &&
                      !isStartDate &&
                      !isEndDate
                        ? isInHoverRange
                          ? "bg-blue-50 text-blue-800 hover:bg-blue-100 border border-blue-200"
                          : "bg-blue-100 text-blue-900 hover:bg-blue-200"
                        : ""
                    }
                    ${
                      isToday &&
                      !isSelected &&
                      !isInRange &&
                      !isInHoverRange &&
                      !isStartDate &&
                      !isEndDate
                        ? "ring-2 ring-brown-400 ring-inset"
                        : ""
                    }
                  `}
                >
                  {date.date()}
                </button>
              );
            })}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-2 mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCancel();
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all duration-200"
            >
              Cancel
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleApply();
              }}
              disabled={!tempStartDate || !tempEndDate}
              className="px-4 py-2 text-sm font-medium text-white bg-orange-500 border border-transparent rounded-md hover:bg-orange-600 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomDateRangePicker;
